import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, RefreshControl, StyleSheet } from 'react-native';

import { useIsFocused, useNavigation, useRoute, useScrollToTop } from '@react-navigation/native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';

import { BottomFooterRG } from '@/components/BottomFooterRG';
import { Button } from '@/components/ButtonComponent';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import {
    ArrayWith5Elements,
    useResponsiveColumnCount,
} from '@/feature/responsive-design/hooks/use-responsive-column-count';
import { CashOutOffersProvider } from '@/hooks/cash-out-offers-context';
import { useFetchOnInitialFocus } from '@/hooks/use-fetch-on-initial-focus';
import { useFetchOnResume } from '@/hooks/use-fetch-resume';
import { useForceRefresh } from '@/hooks/use-force-refresh';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { PredictionLobbyTabNavigationProp } from '@/navigation/prediction/types';
import { EntriesTabNavigationProp } from '@/navigation/sweeps/types';
import { designSystem } from '@/styles/styles';
import { isWeb } from '@/utils/constants-platform-specific';
import { InfiniteData } from '@tanstack/react-query';

import { Bet, GetBetsResponse, OPEN_BET_TABS, TabType } from '../hooks/types';
import { useInfiniteBetsQuery } from '../hooks/use-bets';
import { useCashoutAmountsQuery } from '../hooks/use-cashout';
import { useSbkCashedOutEntries } from '../hooks/use-sbk-cashed-out-entries';
import { useSubscribeEventsMatchUpdate } from '../hooks/use-subscribe-events-match-update';
import { BetsTabParamsList } from '../screens/BetsScreen';
import { BetListSkeleton } from './BetListSkeleton';
import { SbkBetCard } from './SbkBetCard';

export const COLUMN_COUNT: ArrayWith5Elements = [1, 1, 1, 2, 3];
export const COLUMN_GAP = 10;

const styles = StyleSheet.create({
    container: {
        flexGrow: 1,
        justifyContent: 'flex-start',
        paddingTop: 8,
    },
    columns: { gap: COLUMN_GAP },
});

export type BetsParamList = NativeStackScreenProps<BetsTabParamsList, 'Bet-upcoming'>;

export type BetListContentProps = {
    /** Backend `tab` value to fetch. */
    tab: TabType;
    /** Lowercase noun interpolated into the empty-state copy (e.g. `upcoming`, `cashed out`). */
    tabType: string;
    /** Optional element rendered above the list (e.g. the Finished tab's filter-chip row). */
    listHeaderComponent?: React.ReactElement | null;
};

/** Route-param wrapper used by the Upcoming / Live tab screens. */
export const BetList = React.memo(() => {
    const { params } = useRoute<BetsParamList['route']>();
    return <BetListContent tab={params.tab} tabType={params.tabType} />;
});

export const BetListContent = React.memo(({ tab, tabType, listHeaderComponent }: BetListContentProps) => {
    const { navigate: navigateSweeps } = useNavigation<EntriesTabNavigationProp>();
    const { navigate: navigatePredictions } = useNavigation<PredictionLobbyTabNavigationProp>();
    const { t } = useTranslation('bets');
    const columnCount = useResponsiveColumnCount(COLUMN_COUNT);

    const isFocused = useIsFocused();
    const { data, hasNextPage, isFetchingNextPage, fetchNextPage, refetch, isPending } = useInfiniteBetsQuery<
        InfiniteData<GetBetsResponse, unknown>
    >({ tab }, { enabled: isFocused, staleTime: 0 });

    useFetchOnResume(refetch);

    const openBetsTabSelected = OPEN_BET_TABS.includes(tab);

    const renderItem = useCallback(({ item, index }: { item: Bet; index: number }) => {
        return (
            <Box mt={index === 0 ? 's0' : 's16'} flex={1} paddingHorizontal="s16" testID="sbkBetCard">
                <SbkBetCard bet={item} />
            </Box>
        );
    }, []);

    // TODO: to consolidate event formatters when api structure is typing is finalized
    const filteredData = useMemo(() => {
        return data?.pages?.map(page => page.data).flat() || [];
    }, [data]);

    const eventIds = useMemo(() => {
        return [...new Set(filteredData.map(bet => bet.events.map(event => event.id)).flat())];
    }, [filteredData]);

    useSubscribeEventsMatchUpdate(eventIds, openBetsTabSelected);

    // Batch-fetch offers for the eligible open-tab entries, skipping any already cashed out this session.
    const cashedOutEntries = useSbkCashedOutEntries(s => s.entries);
    const eligibleCashOutIds = useMemo(
        () =>
            filteredData
                .filter(bet => bet.result === 'PENDING' && bet.can_be_cashed_out && !cashedOutEntries.has(bet.id))
                .map(bet => bet.id),
        [filteredData, cashedOutEntries]
    );

    const { data: cashOutOffers, refetch: refetchCashOutOffers } = useCashoutAmountsQuery(eligibleCashOutIds);
    // Offers refresh on focus / resume / pull-to-refresh — prices are more volatile than the list itself.
    useFetchOnInitialFocus(refetchCashOutOffers);
    useFetchOnResume(refetchCashOutOffers);

    const refreshAll = useCallback(async () => {
        await Promise.all([refetch(), refetchCashOutOffers()]);
    }, [refetch, refetchCashOutOffers]);
    const { forceRefreshing, onRefresh } = useForceRefresh(refreshAll);
    const isPredictions = useIsPredictionsProduct();

    const listRef = useRef<FlatList<Bet>>(null);
    useScrollToTop(listRef);

    const refreshControl = useMemo(
        () => (
            <RefreshControl
                colors={[designSystem.colors.neutral700]}
                tintColor={ds.colorValue.icon.primary}
                refreshing={forceRefreshing}
                onRefresh={onRefresh}
            />
        ),
        [forceRefreshing, onRefresh]
    );

    const navigateLobby = useCallback(() => {
        if (isPredictions) {
            navigatePredictions('PredictionLobby', { screen: 'PredictionLobbyScreen', params: { leagueId: 'lobby' } });
            return;
        }
        navigateSweeps('SweepsLobby', { screen: 'SweepsLobbyScreen', params: { leagueId: 'lobby' } });
    }, [navigateSweeps, navigatePredictions, isPredictions]);

    const listHeader = useMemo(() => {
        //when filters missing, add extra padding so cards are aligned horizontally across all tabs
        if (openBetsTabSelected) {
            return <SizedBox value={24} />;
        }
        return listHeaderComponent;
    }, [listHeaderComponent, openBetsTabSelected]);

    const emptyFooterMarginTop = isPredictions ? 's32' : 's0';
    const listFooterMarginTop = isPredictions ? 's48' : 's80';
    const footerMarginTop = filteredData.length > 0 ? listFooterMarginTop : emptyFooterMarginTop;

    return (
        <CashOutOffersProvider offers={cashOutOffers}>
            <Box collapsable={false} flex={1}>
                <FlatList
                    key={`${columnCount}`}
                    numColumns={columnCount}
                    scrollEventThrottle={16}
                    data={isPending ? [] : filteredData}
                    renderItem={renderItem}
                    keyExtractor={item => String(item.id)}
                    columnWrapperStyle={columnCount !== 1 ? styles.columns : undefined}
                    contentContainerStyle={styles.container}
                    onEndReachedThreshold={0.5}
                    onEndReached={() => !isFetchingNextPage && hasNextPage && fetchNextPage()}
                    showsVerticalScrollIndicator={isWeb}
                    refreshControl={refreshControl}
                    ListHeaderComponent={listHeader}
                    ListEmptyComponent={
                        isPending ? (
                            <BetListSkeleton />
                        ) : (
                            <Box alignItems="center" justifyContent="center" flex={1} paddingHorizontal="s40">
                                <Text variant="titleLarge">
                                    {isPredictions
                                        ? t('no_positions_heading', { tabType })
                                        : t('no_entries_heading', { tabType })}
                                </Text>
                                <SizedBox value={4} />
                                <Text variant="bodyMedium" color={ds.colorToken.text.secondary} textAlign={'center'}>
                                    {isPredictions
                                        ? t('no_positions_body', { tabType })
                                        : t('no_entries_body', { tabType })}
                                </Text>
                                <SizedBox value={24} />
                                <Button label={t('browse_markets')} shape={'pill'} onPress={navigateLobby} />
                            </Box>
                        )
                    }
                    ListFooterComponent={
                        isPending ? null : <BottomFooterRG footerMarginTop={footerMarginTop} paddingHorizontal="s16" />
                    }
                    ref={listRef}
                />
            </Box>
        </CashOutOffersProvider>
    );
});
